import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and a brief message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Could not send your message. Please call 833-309-LIFE.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 lg:py-28 bg-white" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          <div>
            <p className="text-xs sm:text-sm uppercase tracking-[0.22em] text-gold-deep font-semibold">Get In Touch</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-navy leading-tight mt-3">
              We're Here To Listen, Not To Sell.
            </h2>
            <div className="accent-line mt-5" />
            <p className="mt-6 text-navy/75 text-base sm:text-lg leading-relaxed">
              Have a question about final expense coverage, premiums, or your specific situation? Reach out — a licensed agent
              will get back to you the same business day. No pressure, no obligation.
            </p>

            <div className="mt-10 space-y-6">
              <a href="tel:8333095433" data-testid="contact-phone" className="flex items-start gap-4 group">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-[#0B203B] group-hover:bg-[#152E55] transition-colors">
                  <Phone className="h-5 w-5 text-[#E8C97A]" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Toll-Free</p>
                  <p className="font-serif text-2xl text-navy">833-309-LIFE</p>
                  <p className="text-sm text-muted-foreground">Mon–Fri, 8am–8pm EST</p>
                </div>
              </a>

              <a href="mailto:sales@legacysecuritylife.com" data-testid="contact-email" className="flex items-start gap-4 group">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-[#0B203B] group-hover:bg-[#152E55] transition-colors">
                  <Mail className="h-5 w-5 text-[#E8C97A]" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Email</p>
                  <p className="font-serif text-xl text-navy break-all">sales@legacysecuritylife.com</p>
                </div>
              </a>

              <div data-testid="contact-address" className="flex items-start gap-4">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-[#0B203B]">
                  <MapPin className="h-5 w-5 text-[#E8C97A]" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Office</p>
                  <p className="font-serif text-xl text-navy">401 E Jackson Street, Suite 3300</p>
                  <p className="text-navy/75">Tampa, FL 33602</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#FAFAFA] border border-border rounded-xl p-7 sm:p-9">
            <h3 className="font-serif text-2xl text-navy">Send Us A Message</h3>
            <p className="text-sm text-muted-foreground mt-1">We'll respond within one business day.</p>
            <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="contact-form">
              <div>
                <Label htmlFor="c-name" className="text-navy font-medium">Your Name</Label>
                <Input id="c-name" data-testid="contact-input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12 mt-1.5 text-base border-[#cfd6e0]" placeholder="Jane Doe" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="c-email" className="text-navy font-medium">Email</Label>
                  <Input id="c-email" type="email" data-testid="contact-input-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12 mt-1.5 text-base border-[#cfd6e0]" placeholder="jane@email.com" />
                </div>
                <div>
                  <Label htmlFor="c-phone" className="text-navy font-medium">Phone (optional)</Label>
                  <Input id="c-phone" type="tel" data-testid="contact-input-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-12 mt-1.5 text-base border-[#cfd6e0]" placeholder="(555) 555-5555" />
                </div>
              </div>
              <div>
                <Label htmlFor="c-msg" className="text-navy font-medium">Message</Label>
                <Textarea id="c-msg" data-testid="contact-input-message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 text-base border-[#cfd6e0]" placeholder="Tell us a bit about what you're looking for…" />
              </div>
              <Button type="submit" disabled={loading} data-testid="contact-submit-btn" className="w-full h-12 bg-[#0B203B] hover:bg-[#152E55] text-white font-semibold text-base">
                {loading ? "Sending..." : "Send Message"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
